import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
  Table,
} from 'components';
import { connect } from 'react-redux';
import {
  typeOptions,
  deltaClassOptions,
  organizationSizeOptions,
  organizationIndustryOptions,
} from '../../../../shared/Cis/CisOptions';
import {
  loadIpmaCisCreator,
  setIpmaCisPageCreator,
  setIpmaCisPerPageCreator,
  setIpmaCisSortCreator,
  setIpmaCisFiltersCreator,
  removeIpmaCisFiltersCreator,
  setIpmaCisProfileCreator,
} from 'store/actionsCreators';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import PropTypes from 'prop-types';
import {
  loadGlobalCisDeltaCertificatesAttributesCreator,
  removeIpmaCisCreator,
} from '../../../../store/actionsCreators';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';

const DeltaCertificates = ({
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  removeFilters,
  setProfile,
  removeDeltaCertificate,
  countries,
  organizations,
  loadDeltaCertificatesAttributes,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const manageCisPermission = checkPermissions({ roles: ['ipma_cis'] }, userPermissions || []);

  useEffect(() => {
    if (document.getElementById('ipma-cis-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  useEffect(() => {
    loadDeltaCertificatesAttributes();
  }, []);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.certId || ''}
                  onFilter={(val) => handleColumnFilterChange('certId', val)}
                  filterPlaceholder={t('page.user.cis.deltaCertificates.certIdPlaceholder')}
                  label={t('page.user.cis.deltaCertificates.certId')}
                  sort
                  sortDirection={getSortDirection('certId')}
                  onSort={(dir) => handleColumnSort('certId', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.organizationCountry || ''}
                  onFilter={(val) => handleColumnFilterChange('organizationCountry', val)}
                  filterOptions={countries}
                  filterPlaceholder={t(
                    'page.user.cis.deltaCertificates.organizationCountryPlaceholder',
                  )}
                  label={t('page.user.cis.deltaCertificates.organizationCountry')}
                  sort
                  sortDirection={getSortDirection('organizationCountry')}
                  onSort={(dir) => handleColumnSort('organizationCountry', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.organization || ''}
                  onFilter={(val) => handleColumnFilterChange('organization', val)}
                  filterOptions={organizations}
                  filterPlaceholder={t('page.user.cis.deltaCertificates.organizationPlaceholder')}
                  label={t('page.user.cis.deltaCertificates.organizationName')}
                  sort
                  sortDirection={getSortDirection('organizationName')}
                  onSort={(dir) => handleColumnSort('organizationName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.organizationAddress || ''}
                  onFilter={(val) => handleColumnFilterChange('organizationAddress', val)}
                  filterPlaceholder={t(
                    'page.user.cis.deltaCertificates.organizationAddressPlaceholder',
                  )}
                  label={t('page.user.cis.deltaCertificates.organizationAddress')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.organizationContactPerson || ''}
                  onFilter={(val) => handleColumnFilterChange('organizationContactPerson', val)}
                  filterPlaceholder={t(
                    'page.user.cis.deltaCertificates.organizationContactPersonPlaceholder',
                  )}
                  label={t('page.user.cis.deltaCertificates.organizationContactPerson')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.status || ''}
                  onFilter={(val) => handleColumnFilterChange('status', val)}
                  filterOptions={typeOptions}
                  filterPlaceholder={t('page.user.cis.deltaCertificates.statusPlaceholder')}
                  label={t('page.user.cis.deltaCertificates.status')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.deltaClass || ''}
                  onFilter={(val) => handleColumnFilterChange('deltaClass', val)}
                  filterOptions={deltaClassOptions}
                  filterPlaceholder={t('page.user.cis.deltaCertificates.deltaClassPlaceholder')}
                  label={t('page.user.cis.deltaCertificates.deltaClass')}
                  sort
                  sortDirection={getSortDirection('deltaClass')}
                  onSort={(dir) => handleColumnSort('deltaClass', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range-year-select"
                  filterValueFrom={filters.dateDeltaCertificateFrom || ''}
                  filterValueTo={filters.dateDeltaCertificateTo || ''}
                  filterValueYear={filters.dateDeltaCertificateYear || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('dateDeltaCertificateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('dateDeltaCertificateTo', val)}
                  onFilterYear={(val) => handleColumnFilterChange('dateDeltaCertificateYear', val)}
                  filterPlaceholder={t(
                    'page.user.cis.deltaCertificates.dateDeltaCertificatePlaceholder',
                  )}
                  label={t('page.user.cis.deltaCertificates.dateDeltaCertificate')}
                  sort
                  sortDirection={getSortDirection('dateDeltaCertificate')}
                  onSort={(dir) => handleColumnSort('dateDeltaCertificate', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.validTillFrom || ''}
                  filterValueTo={filters.validTillTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('validTillFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('validTillTo', val)}
                  filterPlaceholder={t('page.user.cis.deltaCertificates.validTillPlaceholder')}
                  label={t('page.user.cis.deltaCertificates.validTill')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.organizationSize || ''}
                  onFilter={(val) => handleColumnFilterChange('organizationSize', val)}
                  filterOptions={organizationSizeOptions}
                  filterPlaceholder={t(
                    'page.user.cis.deltaCertificates.organizationSizePlaceholder',
                  )}
                  label={t('page.user.cis.deltaCertificates.organizationSize')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.organizationIndustries || ''}
                  onFilter={(val) => handleColumnFilterChange('organizationIndustries', val)}
                  filterOptions={organizationIndustryOptions}
                  filterPlaceholder={t(
                    'page.user.cis.deltaCertificates.organizationIndustriesPlaceholder',
                  )}
                  label={t('page.user.cis.deltaCertificates.organizationIndustries')}
                />
              </CisTable.StickyHeading>
              {manageCisPermission && (
                <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                  <TableHeaderCell
                    label={t('page.user.cis.deltaCertificates.actions')}
                    labelAlign="center"
                    isAction={true}
                  />
                </CisTable.StickyActionHeading>
              )}
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={12}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={12}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.deltaCertificates.certId')}:</Table.Label>
                      {item.certId}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.deltaCertificates.organizationCountry')}:
                      </Table.Label>
                      {item.organization?.address?.country?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.deltaCertificates.organizationName')}:
                      </Table.Label>
                      {item.organization?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.deltaCertificates.organizationAddress')}:
                      </Table.Label>
                      {[
                        item.organization?.address?.line,
                        item.organization?.address?.line2,
                        item.organization?.address?.country?.name,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.deltaCertificates.organizationContactPerson')}:
                      </Table.Label>
                      {item.organization?.contactPerson}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.deltaCertificates.status')}:</Table.Label>
                      {item.status}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.deltaCertificates.deltaClass')}:</Table.Label>
                      {item.deltaClass}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.deltaCertificates.dateDeltaCertificate')}:
                      </Table.Label>
                      {item.dateDeltaCertificate &&
                        moment(item.dateDeltaCertificate).format('DD/MM/YYYY')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.deltaCertificates.validTill')}:</Table.Label>
                      {item.validTill && moment(item.validTill).format('DD/MM/YYYY')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.deltaCertificates.organizationSize')}:
                      </Table.Label>
                      {item.organization?.size}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.deltaCertificates.organizationIndustries')}:
                      </Table.Label>
                      {item.organization?.industries}
                    </CisTable.Cell>
                    {manageCisPermission && (
                      <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                        <Table.Label>{t('page.user.cis.deltaCertificates.actions')}:</Table.Label>
                        <Row style={{ gap: '3px', justifyContent: 'center' }}>
                          <ButtonIcon
                            icon="edit"
                            secondary
                            as={Button}
                            onClick={() => {
                              setProfile(item);
                              history.push(`/ipma/cis/delta-certificates/edit`);
                            }}
                          />
                          {item.canRenew && (
                            <ButtonIcon
                              icon="clock"
                              secondary
                              as={Button}
                              onClick={() => {
                                setProfile(item);
                                history.push(`/ipma/cis/delta-certificates/renew`);
                              }}
                            />
                          )}
                          <ButtonIcon
                            icon="trash"
                            onClick={() => {
                              setSelectedItem(item);
                              setOpenConfirmModal(true);
                            }}
                          />
                        </Row>
                      </CisTable.StickyActionCell>
                    )}
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.user.cis.deltaCertificates.confirmDelete')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeDeltaCertificate(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        manageCisPermission &&
        ReactDOM.createPortal(
          <Button
            style={{ width: '30%' }}
            secondary
            as={Link}
            to={`/ipma/cis/delta-certificates/create`}
          >
            {t('page.user.cis.deltaCertificates.new')}
          </Button>,
          document.getElementById('ipma-cis-buttons-portal'),
        )}
    </PageContent>
  );
};

DeltaCertificates.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  organizations: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeDeltaCertificate: PropTypes.func.isRequired,
};

DeltaCertificates.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ipma, globals, user }) => ({
  loading: isLoading(loading, 'LOAD_IPMA_CIS'),
  list: ipma.cis.deltaCertificates,
  countries: globals.countries,
  organizations: globals.cis.organizations,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadIpmaCisCreator('deltaCertificates')),
  setPage: (payload) => dispatch(setIpmaCisPageCreator('deltaCertificates', payload)),
  setPerPage: (payload) => dispatch(setIpmaCisPerPageCreator('deltaCertificates', payload)),
  setSort: (payload) => dispatch(setIpmaCisSortCreator('deltaCertificates', payload)),
  setFilters: (payload) => dispatch(setIpmaCisFiltersCreator('deltaCertificates', payload)),
  removeFilters: () => dispatch(removeIpmaCisFiltersCreator('deltaCertificates')),
  setProfile: (payload) => dispatch(setIpmaCisProfileCreator('deltaCertificates', payload)),
  removeDeltaCertificate: (payload) => dispatch(removeIpmaCisCreator('deltaCertificates', payload)),
  loadDeltaCertificatesAttributes: () =>
    dispatch(loadGlobalCisDeltaCertificatesAttributesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(DeltaCertificates);
